import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { useToast } from '@/hooks/use-toast';
import { ArrowLeft, Users, Play, SkipForward, Trophy, QrCode, Timer, Crown, StopCircle, TrendingUp, BarChart3 } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import QRCode from 'qrcode';

interface GameSessionProps {
  gameId: number;
  onExit: () => void;
}

interface Question {
  id: number;
  question_text: string;
  answer_text: string;
  question_type: string;
  options?: string[];
}

interface Participant {
  id: number;
  player_name: string;
  total_score: number;
  is_connected: boolean;
}

interface GameSessionData {
  id: number;
  game_name: string;
  room_code: string;
  question_ids: string;
  current_question_index: number;
  game_status: string;
  question_time_limit: number;
  question_started_at: string;
}

interface GameStatistics {
  totalParticipants: number;
  averageScore: number;
  questionsCompleted: number;
  gameStartTime: string;
  gameDuration: string;
}

const GameSession: React.FC<GameSessionProps> = ({ gameId, onExit }) => {
  const { toast } = useToast();
  const [gameData, setGameData] = useState<GameSessionData | null>(null);
  const [questions, setQuestions] = useState<Question[]>([]);
  const [participants, setParticipants] = useState<Participant[]>([]);
  const [currentQuestion, setCurrentQuestion] = useState<Question | null>(null);
  const [timeLeft, setTimeLeft] = useState(0);
  const [isShowingResults, setIsShowingResults] = useState(false);
  const [qrCodeUrl, setQrCodeUrl] = useState('');
  const [participantResponses, setParticipantResponses] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showEndGameConfirm, setShowEndGameConfirm] = useState(false);
  const [gameStatistics, setGameStatistics] = useState<GameStatistics | null>(null);
  const [showFinalResults, setShowFinalResults] = useState(false);

  // Use refs to prevent infinite loops
  const gameDataRef = useRef<GameSessionData | null>(null);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  const fetchGameData = useCallback(async () => {
    if (!gameId) return;

    try {
      setLoading(true);
      setError(null);

      const { data, error: gameError } = await window.ezsite.apis.tablePage(17213, {
        PageNo: 1,
        PageSize: 1,
        Filters: [{ name: 'id', op: 'Equal', value: gameId }]
      });

      if (gameError) throw new Error(gameError);
      if (!data.List || data.List.length === 0) {
        throw new Error('Game not found');
      }

      const session = data.List[0];
      setGameData(session);
      gameDataRef.current = session;

      // Fetch questions
      const questionIds = JSON.parse(session.question_ids || '[]');
      if (questionIds.length === 0) {
        throw new Error('No questions found for this game');
      }

      const questionsData = await Promise.all(
        questionIds.map(async (id: number) => {
          const { data: qData, error: qError } = await window.ezsite.apis.tablePage(16057, {
            PageNo: 1,
            PageSize: 1,
            Filters: [{ name: 'id', op: 'Equal', value: id }]
          });
          if (qError) throw new Error(qError);
          return qData.List[0];
        })
      );

      const processedQuestions = questionsData.map((q) => {
        let options = [];
        if (q.question_type === 'multiple_choice') {
          try {
            options = JSON.parse(q.answer_text);
          } catch {
            options = q.answer_text.split(',').map((opt: string) => opt.trim());
          }
        }
        return { ...q, options };
      });

      setQuestions(processedQuestions);
      setCurrentQuestion(processedQuestions[session.current_question_index] || processedQuestions[0]);

      if (session.game_status === 'active') {
        const startTime = new Date(session.question_started_at).getTime();
        const elapsed = Math.floor((Date.now() - startTime) / 1000);
        setTimeLeft(Math.max(0, session.question_time_limit - elapsed));
      }

      setLoading(false);
    } catch (error) {
      console.error('Error fetching game data:', error);
      setError(error instanceof Error ? error.message : 'Failed to load game data');
      setLoading(false);
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to load game data',
        variant: 'destructive'
      });
    }
  }, [gameId, toast]);

  const fetchParticipants = useCallback(async () => {
    if (!gameId) return;

    try {
      const { data, error } = await window.ezsite.apis.tablePage(17214, {
        PageNo: 1,
        PageSize: 100,
        OrderByField: 'total_score',
        IsAsc: false,
        Filters: [{ name: 'game_session_id', op: 'Equal', value: gameId }]
      });

      if (error) throw error;
      setParticipants(data.List);
    } catch (error) {
      console.error('Error fetching participants:', error);
    }
  }, [gameId]);

  const updateGameState = useCallback(async () => {
    if (!gameId || !gameDataRef.current) return;

    try {
      const { data, error } = await window.ezsite.apis.tablePage(17213, {
        PageNo: 1,
        PageSize: 1,
        Filters: [{ name: 'id', op: 'Equal', value: gameId }]
      });

      if (error) throw error;
      const updatedSession = data.List[0];

      // Only update if there's a meaningful change
      if (updatedSession.current_question_index !== gameDataRef.current.current_question_index ||
      updatedSession.game_status !== gameDataRef.current.game_status) {
        setGameData(updatedSession);
        gameDataRef.current = updatedSession;

        if (questions.length > 0) {
          setCurrentQuestion(questions[updatedSession.current_question_index]);
        }
        setIsShowingResults(false);
      }
    } catch (error) {
      console.error('Error updating game state:', error);
    }
  }, [gameId, questions]);

  const generateQRCode = useCallback(async () => {
    if (!gameData) return;

    try {
      const joinUrl = `${window.location.origin}/game-join/${gameData.room_code}`;
      const qrCode = await QRCode.toDataURL(joinUrl, {
        width: 200,
        margin: 2,
        color: {
          dark: '#000000',
          light: '#FFFFFF'
        }
      });
      setQrCodeUrl(qrCode);
    } catch (error) {
      console.error('Error generating QR code:', error);
    }
  }, [gameData]);

  const calculateGameStatistics = useCallback(async () => {
    if (!gameData || participants.length === 0) return;

    const totalParticipants = participants.length;
    const averageScore = participants.reduce((sum, p) => sum + p.total_score, 0) / totalParticipants;
    const questionsCompleted = gameData.current_question_index;
    const gameStartTime = new Date(gameData.question_started_at).toLocaleString();

    // Calculate game duration (simplified - in real scenario you'd track start time differently)
    const now = new Date().getTime();
    const startTime = new Date(gameData.question_started_at).getTime();
    const durationMinutes = Math.floor((now - startTime) / (1000 * 60));
    const gameDuration = `${durationMinutes} minutes`;

    setGameStatistics({
      totalParticipants,
      averageScore: Math.round(averageScore * 100) / 100,
      questionsCompleted,
      gameStartTime,
      gameDuration
    });
  }, [gameData, participants]);

  // Initial load
  useEffect(() => {
    fetchGameData();
  }, [fetchGameData]);

  // Generate QR code when game data changes
  useEffect(() => {
    if (gameData) {
      generateQRCode();
    }
  }, [gameData, generateQRCode]);

  // Set up polling interval
  useEffect(() => {
    if (loading || error) return;

    intervalRef.current = setInterval(() => {
      fetchParticipants();
      updateGameState();
    }, 3000); // Increased to 3 seconds to reduce load

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [loading, error, fetchParticipants, updateGameState]);

  // Timer for active questions
  useEffect(() => {
    if (gameData?.game_status === 'active' && timeLeft > 0) {
      const timer = setInterval(() => {
        setTimeLeft((prev) => {
          if (prev <= 1) {
            showQuestionResults();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(timer);
    }
  }, [gameData?.game_status, timeLeft]);

  // Calculate statistics when game ends
  useEffect(() => {
    if (gameData?.game_status === 'ended' || gameData?.game_status === 'completed') {
      calculateGameStatistics();
    }
  }, [gameData?.game_status, calculateGameStatistics]);

  const startGame = async () => {
    if (!gameData) return;

    try {
      const { error } = await window.ezsite.apis.tableUpdate(17213, {
        id: gameId,
        game_status: 'active',
        current_question_index: 0,
        question_started_at: new Date().toISOString()
      });

      if (error) throw error;

      setTimeLeft(gameData?.question_time_limit || 30);
      toast({
        title: 'Game Started!',
        description: 'The quiz has begun. Players can now answer questions.'
      });
    } catch (error) {
      console.error('Error starting game:', error);
      toast({
        title: 'Error',
        description: 'Failed to start the game',
        variant: 'destructive'
      });
    }
  };

  const endGame = async () => {
    if (!gameData) return;

    try {
      const { error } = await window.ezsite.apis.tableUpdate(17213, {
        id: gameId,
        game_status: 'ended'
      });

      if (error) throw error;

      setShowEndGameConfirm(false);
      setShowFinalResults(true);
      await calculateGameStatistics();

      toast({
        title: 'Game Ended',
        description: 'The game has been ended successfully. Results are now available.'
      });
    } catch (error) {
      console.error('Error ending game:', error);
      toast({
        title: 'Error',
        description: 'Failed to end the game',
        variant: 'destructive'
      });
    }
  };

  const nextQuestion = async () => {
    if (!gameData || !questions) return;

    const nextIndex = gameData.current_question_index + 1;

    if (nextIndex >= questions.length) {
      // End game
      try {
        const { error } = await window.ezsite.apis.tableUpdate(17213, {
          id: gameId,
          game_status: 'completed'
        });

        if (error) throw error;

        toast({
          title: 'Game Completed!',
          description: 'All questions have been answered.'
        });
      } catch (error) {
        console.error('Error ending game:', error);
      }
      return;
    }

    try {
      const { error } = await window.ezsite.apis.tableUpdate(17213, {
        id: gameId,
        current_question_index: nextIndex,
        question_started_at: new Date().toISOString()
      });

      if (error) throw error;

      setCurrentQuestion(questions[nextIndex]);
      setTimeLeft(gameData.question_time_limit);
      setIsShowingResults(false);
    } catch (error) {
      console.error('Error moving to next question:', error);
      toast({
        title: 'Error',
        description: 'Failed to move to next question',
        variant: 'destructive'
      });
    }
  };

  const showQuestionResults = async () => {
    if (!gameData) return;

    try {
      const { data, error } = await window.ezsite.apis.tablePage(17215, {
        PageNo: 1,
        PageSize: 100,
        Filters: [
        { name: 'game_session_id', op: 'Equal', value: gameId },
        { name: 'question_index', op: 'Equal', value: gameData?.current_question_index }]

      });

      if (error) throw error;
      setParticipantResponses(data.List);
      setIsShowingResults(true);
    } catch (error) {
      console.error('Error fetching question results:', error);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen" data-id="7zm0yyvme" data-path="src/components/gameify/GameSession.tsx">
        <div className="text-center" data-id="3cnn9pikn" data-path="src/components/gameify/GameSession.tsx">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4" data-id="kcne31q7d" data-path="src/components/gameify/GameSession.tsx"></div>
          <p data-id="h24t3f4pj" data-path="src/components/gameify/GameSession.tsx">Loading game...</p>
        </div>
      </div>);

  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen" data-id="5tp3vd16x" data-path="src/components/gameify/GameSession.tsx">
        <div className="text-center" data-id="fdh3vmge3" data-path="src/components/gameify/GameSession.tsx">
          <div className="text-red-500 mb-4" data-id="xesdhcwvn" data-path="src/components/gameify/GameSession.tsx">
            <Trophy className="h-12 w-12 mx-auto mb-2" data-id="jyc37zw3i" data-path="src/components/gameify/GameSession.tsx" />
            <h2 className="text-xl font-bold" data-id="jaqvb9cm3" data-path="src/components/gameify/GameSession.tsx">Game Error</h2>
          </div>
          <p className="text-muted-foreground mb-4" data-id="alvya0h7y" data-path="src/components/gameify/GameSession.tsx">{error}</p>
          <div className="space-x-2" data-id="l3psd4qqx" data-path="src/components/gameify/GameSession.tsx">
            <Button onClick={fetchGameData} variant="outline" data-id="wv46fok62" data-path="src/components/gameify/GameSession.tsx">
              Try Again
            </Button>
            <Button onClick={onExit} data-id="ij9td9pgx" data-path="src/components/gameify/GameSession.tsx">
              Back to Games
            </Button>
          </div>
        </div>
      </div>);

  }

  if (!gameData || !currentQuestion) {
    return (
      <div className="flex items-center justify-center min-h-screen" data-id="zqv0yi1l6" data-path="src/components/gameify/GameSession.tsx">
        <div className="text-center" data-id="ovvxax5xn" data-path="src/components/gameify/GameSession.tsx">
          <div className="text-muted-foreground mb-4" data-id="rl2ahx9hn" data-path="src/components/gameify/GameSession.tsx">
            <Trophy className="h-12 w-12 mx-auto mb-2" data-id="rodlrrrre" data-path="src/components/gameify/GameSession.tsx" />
            <h2 className="text-xl font-bold" data-id="dowarai40" data-path="src/components/gameify/GameSession.tsx">Game Not Ready</h2>
          </div>
          <p className="text-muted-foreground mb-4" data-id="fvqjow7um" data-path="src/components/gameify/GameSession.tsx">Unable to load game data</p>
          <Button onClick={onExit} data-id="nuoaoyna2" data-path="src/components/gameify/GameSession.tsx">
            Back to Games
          </Button>
        </div>
      </div>);

  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-pink-50 p-4" data-id="z05gmj182" data-path="src/components/gameify/GameSession.tsx">
      <div className="max-w-6xl mx-auto" data-id="j83b7xydx" data-path="src/components/gameify/GameSession.tsx">
        {/* Header */}
        <div className="flex items-center justify-between mb-6" data-id="1k60spptd" data-path="src/components/gameify/GameSession.tsx">
          <Button variant="outline" onClick={onExit} data-id="932in1cee" data-path="src/components/gameify/GameSession.tsx">
            <ArrowLeft className="h-4 w-4 mr-2" data-id="virj1v8qa" data-path="src/components/gameify/GameSession.tsx" />
            Back to Games
          </Button>
          <div className="text-center" data-id="rzhkye943" data-path="src/components/gameify/GameSession.tsx">
            <h1 className="text-2xl font-bold" data-id="oe4f326mw" data-path="src/components/gameify/GameSession.tsx">{gameData.game_name}</h1>
            <p className="text-muted-foreground" data-id="vsm986wws" data-path="src/components/gameify/GameSession.tsx">Room: {gameData.room_code}</p>
          </div>
          <div className="flex items-center gap-4" data-id="qq6q8swhx" data-path="src/components/gameify/GameSession.tsx">
            <div className="flex items-center gap-2" data-id="g1pwpdz0a" data-path="src/components/gameify/GameSession.tsx">
              <Users className="h-4 w-4" data-id="41vu7flrb" data-path="src/components/gameify/GameSession.tsx" />
              <span data-id="v4sifma5j" data-path="src/components/gameify/GameSession.tsx">{participants.length} players</span>
            </div>
            
            {/* End Game Button - Only show during waiting or active status */}
            {(gameData.game_status === 'waiting' || gameData.game_status === 'active') &&
            <AlertDialog open={showEndGameConfirm} onOpenChange={setShowEndGameConfirm} data-id="v68007u2v" data-path="src/components/gameify/GameSession.tsx">
                <AlertDialogTrigger asChild data-id="7s7p1fmc8" data-path="src/components/gameify/GameSession.tsx">
                  <Button variant="destructive" size="sm" data-id="vjj8unm4r" data-path="src/components/gameify/GameSession.tsx">
                    <StopCircle className="h-4 w-4 mr-2" data-id="p85zyyzh3" data-path="src/components/gameify/GameSession.tsx" />
                    End Game
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent data-id="0og6enm2j" data-path="src/components/gameify/GameSession.tsx">
                  <AlertDialogHeader data-id="e0ycvdvlj" data-path="src/components/gameify/GameSession.tsx">
                    <AlertDialogTitle data-id="5hbkvqy8h" data-path="src/components/gameify/GameSession.tsx">End Game Session?</AlertDialogTitle>
                    <AlertDialogDescription data-id="rqhm0qgzi" data-path="src/components/gameify/GameSession.tsx">
                      This will immediately end the current game session. All participants will be disconnected and final results will be displayed. This action cannot be undone.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter data-id="r5vs0y015" data-path="src/components/gameify/GameSession.tsx">
                    <AlertDialogCancel data-id="u7633z6ty" data-path="src/components/gameify/GameSession.tsx">Cancel</AlertDialogCancel>
                    <AlertDialogAction onClick={endGame} className="bg-red-600 hover:bg-red-700" data-id="1n8sf5f3j" data-path="src/components/gameify/GameSession.tsx">
                      End Game
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            }
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="pem7ejhbm" data-path="src/components/gameify/GameSession.tsx">
          {/* Main Game Area */}
          <div className="lg:col-span-2 space-y-6" data-id="pzefb31om" data-path="src/components/gameify/GameSession.tsx">
            {gameData.game_status === 'waiting' &&
            <Card data-id="i5rujsisy" data-path="src/components/gameify/GameSession.tsx">
                <CardContent className="p-8 text-center" data-id="084zxvf4c" data-path="src/components/gameify/GameSession.tsx">
                  <div className="space-y-6" data-id="x0ohlznws" data-path="src/components/gameify/GameSession.tsx">
                    <div data-id="62z97pzt0" data-path="src/components/gameify/GameSession.tsx">
                      <h2 className="text-2xl font-bold mb-4" data-id="ff56fg5ij" data-path="src/components/gameify/GameSession.tsx">Waiting for Players</h2>
                      <div className="flex items-center justify-center gap-4 mb-6" data-id="2e3uqf0bu" data-path="src/components/gameify/GameSession.tsx">
                        <div className="bg-white p-4 rounded-lg shadow-lg" data-id="vrunq1gdj" data-path="src/components/gameify/GameSession.tsx">
                          {qrCodeUrl && <img src={qrCodeUrl} alt="QR Code" className="w-48 h-48" data-id="s61jp6vb3" data-path="src/components/gameify/GameSession.tsx" />}
                        </div>
                        <div className="text-left" data-id="lj7uanglu" data-path="src/components/gameify/GameSession.tsx">
                          <p className="text-sm text-muted-foreground mb-2" data-id="5lw4lrh9e" data-path="src/components/gameify/GameSession.tsx">Players can scan this QR code or enter:</p>
                          <div className="text-4xl font-mono font-bold text-purple-600" data-id="t7judcp3z" data-path="src/components/gameify/GameSession.tsx">
                            {gameData.room_code}
                          </div>
                          <p className="text-sm text-muted-foreground mt-2" data-id="dy3q1pylg" data-path="src/components/gameify/GameSession.tsx">
                            Go to: {window.location.origin}/game-join
                          </p>
                        </div>
                      </div>
                    </div>
                    <Button onClick={startGame} size="lg" disabled={participants.length === 0} data-id="zydmht82f" data-path="src/components/gameify/GameSession.tsx">
                      <Play className="h-5 w-5 mr-2" data-id="m7eyxh72g" data-path="src/components/gameify/GameSession.tsx" />
                      Start Game ({participants.length} players)
                    </Button>
                  </div>
                </CardContent>
              </Card>
            }

            {gameData.game_status === 'active' && !isShowingResults &&
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="space-y-6" data-id="948zfskom" data-path="src/components/gameify/GameSession.tsx">
                <Card data-id="yjs4av6mz" data-path="src/components/gameify/GameSession.tsx">
                  <CardHeader data-id="kufgy7sa3" data-path="src/components/gameify/GameSession.tsx">
                    <div className="flex items-center justify-between" data-id="5e119ucy3" data-path="src/components/gameify/GameSession.tsx">
                      <CardTitle className="flex items-center gap-2" data-id="0vakvtocn" data-path="src/components/gameify/GameSession.tsx">
                        <Timer className="h-5 w-5" data-id="rasdcgiqg" data-path="src/components/gameify/GameSession.tsx" />
                        Question {(gameData.current_question_index || 0) + 1} of {questions.length}
                      </CardTitle>
                      <div className="text-2xl font-bold text-purple-600" data-id="ujnblxo4w" data-path="src/components/gameify/GameSession.tsx">
                        {timeLeft}s
                      </div>
                    </div>
                    <Progress value={timeLeft / gameData.question_time_limit * 100} className="h-2" data-id="4qs5a65al" data-path="src/components/gameify/GameSession.tsx" />
                  </CardHeader>
                  <CardContent data-id="pem7s4jjf" data-path="src/components/gameify/GameSession.tsx">
                    <h2 className="text-2xl font-bold mb-6" data-id="s9ny7zykq" data-path="src/components/gameify/GameSession.tsx">{currentQuestion.question_text}</h2>
                    
                    {currentQuestion.question_type === 'multiple_choice' && currentQuestion.options &&
                  <div className="grid grid-cols-2 gap-4" data-id="orkg6vl2u" data-path="src/components/gameify/GameSession.tsx">
                        {currentQuestion.options.map((option, index) =>
                    <div
                      key={index}
                      className="p-4 bg-gradient-to-r from-purple-100 to-pink-100 rounded-lg border-2 border-purple-200" data-id="il9doapxk" data-path="src/components/gameify/GameSession.tsx">
                            <div className="flex items-center gap-3" data-id="xom9ov21a" data-path="src/components/gameify/GameSession.tsx">
                              <div className="w-8 h-8 bg-purple-600 text-white rounded-full flex items-center justify-center font-bold" data-id="v476dbp97" data-path="src/components/gameify/GameSession.tsx">
                                {String.fromCharCode(65 + index)}
                              </div>
                              <span className="text-lg font-medium" data-id="tuy7nymec" data-path="src/components/gameify/GameSession.tsx">{option}</span>
                            </div>
                          </div>
                    )}
                      </div>
                  }
                    
                    {currentQuestion.question_type === 'true_false' &&
                  <div className="grid grid-cols-2 gap-4" data-id="hnfvbzkxy" data-path="src/components/gameify/GameSession.tsx">
                        <div className="p-4 bg-gradient-to-r from-green-100 to-green-200 rounded-lg border-2 border-green-300" data-id="x9yj589ar" data-path="src/components/gameify/GameSession.tsx">
                          <div className="flex items-center gap-3" data-id="uhlu9dqfw" data-path="src/components/gameify/GameSession.tsx">
                            <div className="w-8 h-8 bg-green-600 text-white rounded-full flex items-center justify-center font-bold" data-id="6j3sa38u2" data-path="src/components/gameify/GameSession.tsx">
                              T
                            </div>
                            <span className="text-lg font-medium" data-id="5sbpaf078" data-path="src/components/gameify/GameSession.tsx">True</span>
                          </div>
                        </div>
                        <div className="p-4 bg-gradient-to-r from-red-100 to-red-200 rounded-lg border-2 border-red-300" data-id="l1rgcvx1c" data-path="src/components/gameify/GameSession.tsx">
                          <div className="flex items-center gap-3" data-id="fgmx7r52t" data-path="src/components/gameify/GameSession.tsx">
                            <div className="w-8 h-8 bg-red-600 text-white rounded-full flex items-center justify-center font-bold" data-id="vljm78pv6" data-path="src/components/gameify/GameSession.tsx">
                              F
                            </div>
                            <span className="text-lg font-medium" data-id="ntbr08h0h" data-path="src/components/gameify/GameSession.tsx">False</span>
                          </div>
                        </div>
                      </div>
                  }
                  </CardContent>
                </Card>

                <div className="flex justify-center" data-id="1r33vasxy" data-path="src/components/gameify/GameSession.tsx">
                  <Button onClick={showQuestionResults} variant="outline" data-id="j2v014obe" data-path="src/components/gameify/GameSession.tsx">
                    Show Results
                  </Button>
                </div>
              </motion.div>
            }

            {isShowingResults &&
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="space-y-6" data-id="dgrtkl6je" data-path="src/components/gameify/GameSession.tsx">
                <Card data-id="x2xvo7lkf" data-path="src/components/gameify/GameSession.tsx">
                  <CardHeader data-id="tb5pmtm6a" data-path="src/components/gameify/GameSession.tsx">
                    <CardTitle className="flex items-center gap-2" data-id="1tsdlw30f" data-path="src/components/gameify/GameSession.tsx">
                      <Trophy className="h-5 w-5" data-id="bt4fbqwcw" data-path="src/components/gameify/GameSession.tsx" />
                      Question Results
                    </CardTitle>
                  </CardHeader>
                  <CardContent data-id="kr24k290l" data-path="src/components/gameify/GameSession.tsx">
                    <div className="space-y-4" data-id="hcs3t0bs3" data-path="src/components/gameify/GameSession.tsx">
                      <div className="text-center" data-id="r5k9riq0w" data-path="src/components/gameify/GameSession.tsx">
                        <h3 className="text-xl font-bold mb-2" data-id="jmbcohk2z" data-path="src/components/gameify/GameSession.tsx">{currentQuestion.question_text}</h3>
                        <p className="text-green-600 font-semibold" data-id="1i4yl20mm" data-path="src/components/gameify/GameSession.tsx">
                          Correct Answer: {currentQuestion.answer_text}
                        </p>
                      </div>
                      
                      <div className="grid gap-2" data-id="6io1k2u7x" data-path="src/components/gameify/GameSession.tsx">
                        {participantResponses.map((response, index) =>
                      <div
                        key={index}
                        className={`p-3 rounded-lg flex items-center justify-between ${
                        response.is_correct ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'} border`
                        } data-id="21196upm5" data-path="src/components/gameify/GameSession.tsx">
                            <span className="font-medium" data-id="7kfz97ug9" data-path="src/components/gameify/GameSession.tsx">{response.player_name}</span>
                            <div className="flex items-center gap-2" data-id="ihchp8gtk" data-path="src/components/gameify/GameSession.tsx">
                              <span className="text-sm" data-id="xo04oum4n" data-path="src/components/gameify/GameSession.tsx">{response.selected_answer}</span>
                              <Badge variant={response.is_correct ? 'default' : 'destructive'} data-id="g5llkcofp" data-path="src/components/gameify/GameSession.tsx">
                                {response.is_correct ? `+${response.points_earned}` : '0'} pts
                              </Badge>
                            </div>
                          </div>
                      )}
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <div className="flex justify-center" data-id="96w21lp2e" data-path="src/components/gameify/GameSession.tsx">
                  <Button onClick={nextQuestion} data-id="m12ssu7xf" data-path="src/components/gameify/GameSession.tsx">
                    {(gameData.current_question_index || 0) + 1 >= questions.length ?
                  'End Game' :
                  <>
                        <SkipForward className="h-4 w-4 mr-2" data-id="lmmnzxuhr" data-path="src/components/gameify/GameSession.tsx" />
                        Next Question
                      </>
                  }
                  </Button>
                </div>
              </motion.div>
            }

            {(gameData.game_status === 'completed' || gameData.game_status === 'ended') &&
            <div className="space-y-6" data-id="ikiihjnej" data-path="src/components/gameify/GameSession.tsx">
                <Card data-id="f0smdzi9n" data-path="src/components/gameify/GameSession.tsx">
                  <CardContent className="p-8 text-center" data-id="jbj5ykrmn" data-path="src/components/gameify/GameSession.tsx">
                    <Trophy className="h-16 w-16 mx-auto text-yellow-500 mb-4" data-id="a7tkzlyah" data-path="src/components/gameify/GameSession.tsx" />
                    <h2 className="text-2xl font-bold mb-2" data-id="mu1x3h3bx" data-path="src/components/gameify/GameSession.tsx">
                      {gameData.game_status === 'ended' ? 'Game Ended!' : 'Game Completed!'}
                    </h2>
                    <p className="text-muted-foreground" data-id="e8jei6skc" data-path="src/components/gameify/GameSession.tsx">
                      {gameData.game_status === 'ended' ?
                    'The game was ended by the host. Check the final results below.' :
                    'Thanks for playing! Check the leaderboard to see the final results.'
                    }
                    </p>
                  </CardContent>
                </Card>

                {/* Final Game Statistics */}
                {gameStatistics &&
              <Card data-id="bxub0zjta" data-path="src/components/gameify/GameSession.tsx">
                    <CardHeader data-id="xtwo012vi" data-path="src/components/gameify/GameSession.tsx">
                      <CardTitle className="flex items-center gap-2" data-id="0yikzvo3f" data-path="src/components/gameify/GameSession.tsx">
                        <BarChart3 className="h-5 w-5" data-id="3dj4ebpj8" data-path="src/components/gameify/GameSession.tsx" />
                        Game Statistics
                      </CardTitle>
                    </CardHeader>
                    <CardContent data-id="e5efp93oo" data-path="src/components/gameify/GameSession.tsx">
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-id="tvdog1ai1" data-path="src/components/gameify/GameSession.tsx">
                        <div className="text-center p-4 bg-blue-50 rounded-lg" data-id="tzdl8i5oj" data-path="src/components/gameify/GameSession.tsx">
                          <div className="text-2xl font-bold text-blue-600" data-id="iu2mnub8c" data-path="src/components/gameify/GameSession.tsx">{gameStatistics.totalParticipants}</div>
                          <div className="text-sm text-muted-foreground" data-id="kv2pf69bk" data-path="src/components/gameify/GameSession.tsx">Total Players</div>
                        </div>
                        <div className="text-center p-4 bg-green-50 rounded-lg" data-id="xtuojmlix" data-path="src/components/gameify/GameSession.tsx">
                          <div className="text-2xl font-bold text-green-600" data-id="58faoe49p" data-path="src/components/gameify/GameSession.tsx">{gameStatistics.averageScore}</div>
                          <div className="text-sm text-muted-foreground" data-id="3ih1moupu" data-path="src/components/gameify/GameSession.tsx">Average Score</div>
                        </div>
                        <div className="text-center p-4 bg-purple-50 rounded-lg" data-id="odzaa4wbg" data-path="src/components/gameify/GameSession.tsx">
                          <div className="text-2xl font-bold text-purple-600" data-id="cip3kxwui" data-path="src/components/gameify/GameSession.tsx">{gameStatistics.questionsCompleted}</div>
                          <div className="text-sm text-muted-foreground" data-id="qxvok6jf1" data-path="src/components/gameify/GameSession.tsx">Questions Completed</div>
                        </div>
                        <div className="text-center p-4 bg-orange-50 rounded-lg" data-id="v2d6skfi5" data-path="src/components/gameify/GameSession.tsx">
                          <div className="text-2xl font-bold text-orange-600" data-id="j36qnr41m" data-path="src/components/gameify/GameSession.tsx">{gameStatistics.gameDuration}</div>
                          <div className="text-sm text-muted-foreground" data-id="8hk3bn92m" data-path="src/components/gameify/GameSession.tsx">Game Duration</div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
              }
              </div>
            }
          </div>

          {/* Sidebar - Participants & Leaderboard */}
          <div className="space-y-6" data-id="hkpisy8ps" data-path="src/components/gameify/GameSession.tsx">
            <Card data-id="reh021639" data-path="src/components/gameify/GameSession.tsx">
              <CardHeader data-id="vgea4rwi6" data-path="src/components/gameify/GameSession.tsx">
                <CardTitle className="flex items-center gap-2" data-id="l3dvbnvn8" data-path="src/components/gameify/GameSession.tsx">
                  <Users className="h-5 w-5" data-id="zf66ozn9g" data-path="src/components/gameify/GameSession.tsx" />
                  Leaderboard
                </CardTitle>
              </CardHeader>
              <CardContent data-id="qkrtazdft" data-path="src/components/gameify/GameSession.tsx">
                <div className="space-y-3" data-id="4ux7robwi" data-path="src/components/gameify/GameSession.tsx">
                  {participants.map((participant, index) =>
                  <motion.div
                    key={participant.id}
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className="flex items-center justify-between p-3 bg-gradient-to-r from-purple-50 to-pink-50 rounded-lg" data-id="d125e1lmt" data-path="src/components/gameify/GameSession.tsx">
                      <div className="flex items-center gap-3" data-id="6dt76pp69" data-path="src/components/gameify/GameSession.tsx">
                        <div className="flex items-center justify-center w-8 h-8 bg-purple-600 text-white rounded-full text-sm font-bold" data-id="6j3l4k1fe" data-path="src/components/gameify/GameSession.tsx">
                          {index + 1}
                        </div>
                        {index === 0 && <Crown className="h-4 w-4 text-yellow-500" data-id="xuiqqikx8" data-path="src/components/gameify/GameSession.tsx" />}
                        <span className="font-medium" data-id="j0y02mc49" data-path="src/components/gameify/GameSession.tsx">{participant.player_name}</span>
                        {!participant.is_connected &&
                      <Badge variant="secondary" className="text-xs" data-id="ayla8at35" data-path="src/components/gameify/GameSession.tsx">
                            Offline
                          </Badge>
                      }
                      </div>
                      <div className="text-right" data-id="3zwq4gebs" data-path="src/components/gameify/GameSession.tsx">
                        <div className="text-lg font-bold text-purple-600" data-id="fy30kw3te" data-path="src/components/gameify/GameSession.tsx">
                          {participant.total_score}
                        </div>
                        <div className="text-xs text-muted-foreground" data-id="qegj2wu8e" data-path="src/components/gameify/GameSession.tsx">points</div>
                      </div>
                    </motion.div>
                  )}
                </div>

                {participants.length === 0 &&
                <div className="text-center py-8 text-muted-foreground" data-id="0hbcgu3a5" data-path="src/components/gameify/GameSession.tsx">
                    <Users className="h-12 w-12 mx-auto mb-4 opacity-50" data-id="kxnqvolj9" data-path="src/components/gameify/GameSession.tsx" />
                    <p data-id="af5dc7v7e" data-path="src/components/gameify/GameSession.tsx">No players yet</p>
                    <p className="text-sm" data-id="7jevbdkpy" data-path="src/components/gameify/GameSession.tsx">Share the room code to get started!</p>
                  </div>
                }
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>);

};

export default GameSession;